import { useEffect, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { ExceptionsListing, ExceptionsListingProps } from 'Components/Exceptions/ExceptionsListing/ExceptionsListing';
import { AssociatedControlsModal } from 'Components/Modal/AssociatedControlsModal/AssociatedControlsModal';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { OrganizationResponseWithAssessmentSubjectsBase } from 'Models/AssessmentSubject';
import { ExceptionBreadcrumbSource, ExceptionResponse } from 'Models/Exceptions';
import { OperationalControl } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { UserResponse } from 'Models/User';

export interface ExceptionsTabOperationalControlsProps {
    exceptionsType: ProductModule.CONTROLS;
    exceptionsApi: ExceptionsApi;
    documentApi: DocumentApi;
    users: UserResponse[];
}

export interface ExceptionsTabDueDiligenceProps {
    exceptionsType: DueDiligenceModule;
    exceptionsApi: ExceptionsApi;
    documentApi: DocumentApi;
    users: UserResponse[];
    organizations: OrganizationResponseWithAssessmentSubjectsBase[];
    organizationIdQueryParam?: string;
}

export type ExceptionsTabProps = ExceptionsTabOperationalControlsProps | ExceptionsTabDueDiligenceProps;

/**
 * Renders a table of exceptions after fetching them via the API.
 */
export const ExceptionsTab = (props: ExceptionsTabProps) => {
    const [exceptions, setExceptions] = useState<ExceptionResponse[]>();
    const [mappedControlsToDisplay, setMappedControlsToDisplay] = useState<OperationalControl[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    useEffect(() => {
        const getExceptions = async (): Promise<void> => {
            try {
                const response = props.exceptionsType === ProductModule.CONTROLS ? await props.exceptionsApi.getAllControlExceptions() : await props.exceptionsApi.getAllDueDiligenceExceptions();
                setExceptions(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        getExceptions();
    }, [props.exceptionsApi, props.exceptionsType]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (!exceptions) {
        return <Placeholder />;
    }

    const exceptionsListingProps: ExceptionsListingProps = {
        ...(props.exceptionsType === ProductModule.CONTROLS
            ? {
                  type: ProductModule.CONTROLS,
                  exceptions: exceptions as ExceptionResponse[],
                  displayMappedControlsModal: setMappedControlsToDisplay,
                  createButtonVariant: 'primaryButton',
              }
            : {
                  type: props.exceptionsType,
                  exceptions: exceptions as ExceptionResponse[],
                  documentApi: props.documentApi,
                  organizations: props.organizations,
                  preselectedOrganizationIdForFilter: props.organizationIdQueryParam,
                  createButtonVariant: 'primaryButton',
              }),
        createExceptionOrigin: { source: ExceptionBreadcrumbSource.EXCEPTIONS_LISTING, module: props.exceptionsType },
        hideTitle: true,
    };

    return (
        <>
            {mappedControlsToDisplay && <AssociatedControlsModal associatedControls={mappedControlsToDisplay} hideModal={() => setMappedControlsToDisplay(undefined)} />}
            <ExceptionsListing {...exceptionsListingProps} />
        </>
    );
};
